import React, { useState, useEffect } from 'react';
import { Trash2, Plus, Edit2, Check, X, Building2, Tag, XCircle } from 'lucide-react';
import Modal from '../../components/Modal';
import { apiClient } from '../../config/api';

const AdminOEMsPage = () => {
    const [oems, setOems] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newOem, setNewOem] = useState({ 
        name: '', 
        description: '', 
        website_url: '',
        logo_url: '',
        is_active: true 
    });
    const [editingOem, setEditingOem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedOemId, setExpandedOemId] = useState(null);
    const [oemDetails, setOemDetails] = useState({});
    const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);
    const [selectedOemForBrand, setSelectedOemForBrand] = useState(null);
    const [allBrands, setAllBrands] = useState([]);
    const [newBrandName, setNewBrandName] = useState('');
    const [selectedExistingBrandId, setSelectedExistingBrandId] = useState('');
    const [brandModalMode, setBrandModalMode] = useState('new'); // 'new' or 'existing'

    useEffect(() => {
        fetchOEMs();
        fetchAllBrands();
    }, []);

    const fetchAllBrands = async () => {
        try {
            const data = await apiClient.get('/brands');
            setAllBrands(data || []);
        } catch (error) {
            console.error('Error fetching all brands:', error);
        }
    };

    const fetchOEMs = async () => {
        try {
            setLoading(true);
            const data = await apiClient.get('/oems');
            setOems(data || []);
        } catch (error) {
            console.error('Error fetching OEMs:', error);
            setError('Failed to fetch OEMs');
        } finally {
            setLoading(false);
        }
    };

    const fetchOemDetails = async (oemId) => {
        try {
            const data = await apiClient.get(`/oems/${oemId}`);
            setOemDetails(prev => ({ ...prev, [oemId]: data }));
        } catch (error) {
            console.error('Error fetching OEM details:', error);
        }
    };

    const handleExpand = (oemId) => {
        if (expandedOemId === oemId) {
            setExpandedOemId(null);
        } else {
            setExpandedOemId(oemId);
            if (!oemDetails[oemId]) {
                fetchOemDetails(oemId);
            }
        }
    };

    const handleAdd = async () => {
        if (!newOem.name || !newOem.name.trim()) {
            setError('OEM name is required');
            return;
        }

        try {
            setError('');
            const data = await apiClient.post('/oems', newOem);
            setOems([...oems, data]);
            setIsModalOpen(false);
            setNewOem({ name: '', description: '', website_url: '', logo_url: '', is_active: true });
        } catch (error) {
            console.error('Error creating OEM:', error);
            setError(error.message || 'Failed to create OEM');
        }
    };

    const handleEdit = async () => {
        if (!editingOem.name || !editingOem.name.trim()) {
            setError('OEM name is required');
            return;
        }

        try {
            setError('');
            const data = await apiClient.put(`/oems/${editingOem.id}`, editingOem);
            setOems(oems.map(o => o.id === editingOem.id ? data : o));
            setEditingOem(null);
        } catch (error) {
            console.error('Error updating OEM:', error);
            setError(error.message || 'Failed to update OEM');
        }
    };

    const handleDelete = async (oemId) => {
        if (!window.confirm('Are you sure you want to delete this OEM? Associated groups and brands will have their OEM relationship removed.')) {
            return;
        }

        try {
            await apiClient.delete(`/oems/${oemId}`);
            setOems(oems.filter(o => o.id !== oemId));
            if (expandedOemId === oemId) {
                setExpandedOemId(null);
                setOemDetails(prev => {
                    const newDetails = { ...prev };
                    delete newDetails[oemId];
                    return newDetails;
                });
            }
        } catch (error) {
            console.error('Error deleting OEM:', error);
            alert(error.message || 'Failed to delete OEM');
        }
    };

    const handleAddBrand = (oemId) => {
        setSelectedOemForBrand(oemId);
        setNewBrandName('');
        setSelectedExistingBrandId('');
        setBrandModalMode('new');
        setError('');
        setIsBrandModalOpen(true);
    };

    const handleSaveBrand = async () => {
        if (brandModalMode === 'new') {
            if (!newBrandName || !newBrandName.trim()) {
                setError('Brand name is required');
                return;
            }

            try {
                setError('');
                await apiClient.post('/brands', {
                    name: newBrandName.trim(),
                    oem_id: selectedOemForBrand
                });
                await fetchOemDetails(selectedOemForBrand);
                await fetchAllBrands();
                setIsBrandModalOpen(false);
                setNewBrandName('');
            } catch (error) {
                console.error('Error creating brand:', error);
                setError(error.message || 'Failed to create brand');
            }
        } else {
            if (!selectedExistingBrandId) {
                setError('Please select a brand');
                return;
            }

            try {
                setError('');
                const brand = allBrands.find(b => b.id === selectedExistingBrandId);
                if (!brand) {
                    setError('Selected brand not found');
                    return;
                }

                await apiClient.put(`/brands/${selectedExistingBrandId}`, {
                    name: brand.name,
                    oem_id: selectedOemForBrand
                });
                await fetchOemDetails(selectedOemForBrand);
                await fetchAllBrands();
                setIsBrandModalOpen(false);
                setSelectedExistingBrandId('');
            } catch (error) {
                console.error('Error assigning brand:', error);
                setError(error.message || 'Failed to assign brand to OEM');
            }
        }
    };

    const handleRemoveBrand = async (oemId, brandId) => {
        if (!window.confirm('Are you sure you want to remove this brand from the OEM? The brand will be assigned to "Independent" OEM instead.')) {
            return;
        }

        try {
            const brand = allBrands.find(b => b.id === brandId);
            if (!brand) {
                alert('Brand not found');
                return;
            }

            // Find the "Independent" OEM to assign the brand to
            const oems = await apiClient.get('/oems');
            const independentOem = oems.find(o => o.name === 'Independent');
            
            if (!independentOem) {
                alert('Could not find "Independent" OEM. Please create it first or contact support.');
                return;
            }

            await apiClient.put(`/brands/${brandId}`, {
                name: brand.name,
                oem_id: independentOem.id
            });
            await fetchOemDetails(oemId);
            await fetchAllBrands();
        } catch (error) {
            console.error('Error removing brand:', error);
            alert(error.message || 'Failed to remove brand from OEM. Please check the console for details.');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500">Loading OEMs...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">OEM Management</h1>
                    <p className="text-gray-600 mt-1">Manage Original Equipment Manufacturers (OEMs)</p>
                </div>
                <button
                    onClick={() => {
                        setNewOem({ name: '', description: '', website_url: '', logo_url: '', is_active: true });
                        setError('');
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    <Plus size={18} />
                    Add OEM
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                </div>
            )}

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                OEM Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Description
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Groups
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Brands
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {oems.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                                    No OEMs found. Click "Add OEM" to create one.
                                </td>
                            </tr>
                        ) : (
                            oems.map((oem) => (
                                <React.Fragment key={oem.id}>
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <Building2 size={18} className="text-gray-400 mr-2" />
                                                <span className="font-medium text-gray-900">{oem.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-500 max-w-md truncate">
                                                {oem.description || '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-sm text-gray-900">{oem.group_count || 0}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-sm text-gray-900">{oem.brand_count || 0}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs rounded-full ${
                                                oem.is_active 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {oem.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleExpand(oem.id)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                >
                                                    {expandedOemId === oem.id ? 'Collapse' : 'View Details'}
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setEditingOem({ ...oem });
                                                        setError('');
                                                    }}
                                                    className="text-indigo-600 hover:text-indigo-900"
                                                >
                                                    <Edit2 size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(oem.id)}
                                                    className="text-red-600 hover:text-red-900"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {expandedOemId === oem.id && oemDetails[oem.id] && (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-4 bg-gray-50">
                                                <div className="space-y-4">
                                                    <div>
                                                        <h3 className="font-medium text-gray-900 mb-2">Associated Groups ({oemDetails[oem.id].groups?.length || 0})</h3>
                                                        {oemDetails[oem.id].groups && oemDetails[oem.id].groups.length > 0 ? (
                                                            <div className="flex flex-wrap gap-2">
                                                                {oemDetails[oem.id].groups.map(group => (
                                                                    <span key={group.id} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                                                                        {group.name}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-gray-500">No groups associated with this OEM</p>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center justify-between mb-2">
                                                            <h3 className="font-medium text-gray-900">Associated Brands ({oemDetails[oem.id].brands?.length || 0})</h3>
                                                            <button
                                                                onClick={() => handleAddBrand(oem.id)}
                                                                className="flex items-center gap-1 px-2 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                                                            >
                                                                <Plus size={14} />
                                                                Add Brand
                                                            </button>
                                                        </div>
                                                        {oemDetails[oem.id].brands && oemDetails[oem.id].brands.length > 0 ? (
                                                            <div className="flex flex-wrap gap-2">
                                                                {oemDetails[oem.id].brands.map(brand => (
                                                                    <span key={brand.id} className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm flex items-center gap-1">
                                                                        <Tag size={12} />
                                                                        {brand.name}
                                                                        <button
                                                                            onClick={() => handleRemoveBrand(oem.id, brand.id)}
                                                                            className="ml-1 text-red-600 hover:text-red-800"
                                                                            title="Remove brand from OEM"
                                                                        >
                                                                            <XCircle size={14} />
                                                                        </button>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-gray-500">No brands associated with this OEM</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Add/Edit Modal */}
            <Modal
                isOpen={isModalOpen || editingOem !== null}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingOem(null);
                    setError('');
                }}
                title={editingOem ? 'Edit OEM' : 'Add New OEM'}
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            OEM Name *
                        </label>
                        <input
                            type="text"
                            value={editingOem ? editingOem.name : newOem.name}
                            onChange={(e) => {
                                if (editingOem) {
                                    setEditingOem({ ...editingOem, name: e.target.value });
                                } else {
                                    setNewOem({ ...newOem, name: e.target.value });
                                }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., Volkswagen Group"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                        </label>
                        <textarea
                            value={editingOem ? editingOem.description || '' : newOem.description || ''}
                            onChange={(e) => {
                                if (editingOem) {
                                    setEditingOem({ ...editingOem, description: e.target.value });
                                } else {
                                    setNewOem({ ...newOem, description: e.target.value });
                                }
                            }}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., VAG - Owns VW, Audi, Skoda, Seat, Porsche, Bentley, Lamborghini, Bugatti"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Website URL
                        </label>
                        <input
                            type="url"
                            value={editingOem ? editingOem.website_url || '' : newOem.website_url || ''}
                            onChange={(e) => {
                                if (editingOem) {
                                    setEditingOem({ ...editingOem, website_url: e.target.value });
                                } else {
                                    setNewOem({ ...newOem, website_url: e.target.value });
                                }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            placeholder="https://www.example.com"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Logo URL
                        </label>
                        <input
                            type="url"
                            value={editingOem ? editingOem.logo_url || '' : newOem.logo_url || ''}
                            onChange={(e) => {
                                if (editingOem) {
                                    setEditingOem({ ...editingOem, logo_url: e.target.value });
                                } else {
                                    setNewOem({ ...newOem, logo_url: e.target.value });
                                }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            placeholder="https://www.example.com/logo.png"
                        />
                    </div>

                    <div>
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={editingOem ? editingOem.is_active : newOem.is_active}
                                onChange={(e) => {
                                    if (editingOem) {
                                        setEditingOem({ ...editingOem, is_active: e.target.checked });
                                    } else {
                                        setNewOem({ ...newOem, is_active: e.target.checked });
                                    }
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700">Active</span>
                        </label>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                            {error}
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            onClick={() => {
                                setIsModalOpen(false);
                                setEditingOem(null);
                                setError('');
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={editingOem ? handleEdit : handleAdd}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                        >
                            {editingOem ? (
                                <>
                                    <Check size={18} />
                                    Update
                                </>
                            ) : (
                                <>
                                    <Plus size={18} />
                                    Create
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Add Brand Modal */}
            <Modal
                isOpen={isBrandModalOpen}
                onClose={() => {
                    setIsBrandModalOpen(false);
                    setSelectedOemForBrand(null);
                    setNewBrandName('');
                    setSelectedExistingBrandId('');
                    setBrandModalMode('new');
                    setError('');
                }}
                title="Add Brand to OEM"
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Add Mode
                        </label>
                        <div className="flex gap-4">
                            <label className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    value="new"
                                    checked={brandModalMode === 'new'}
                                    onChange={(e) => {
                                        setBrandModalMode(e.target.value);
                                        setError('');
                                    }}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">Create New Brand</span>
                            </label>
                            <label className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    value="existing"
                                    checked={brandModalMode === 'existing'}
                                    onChange={(e) => {
                                        setBrandModalMode(e.target.value);
                                        setError('');
                                    }}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">Assign Existing Brand</span>
                            </label>
                        </div>
                    </div>

                    {brandModalMode === 'new' ? (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Brand Name *
                            </label>
                            <input
                                type="text"
                                value={newBrandName}
                                onChange={(e) => {
                                    setNewBrandName(e.target.value);
                                    setError('');
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g., Volkswagen"
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSaveBrand();
                                    }
                                }}
                            />
                        </div>
                    ) : (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Select Brand *
                            </label>
                            <select
                                value={selectedExistingBrandId}
                                onChange={(e) => {
                                    setSelectedExistingBrandId(e.target.value);
                                    setError('');
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">-- Select a brand --</option>
                                {allBrands
                                    .filter(brand => brand.oem_id !== selectedOemForBrand)
                                    .map(brand => (
                                        <option key={brand.id} value={brand.id}>
                                            {brand.name} {brand.oem_id ? `(Currently assigned to another OEM)` : '(Unassigned)'}
                                        </option>
                                    ))}
                            </select>
                            {allBrands.filter(brand => brand.oem_id !== selectedOemForBrand).length === 0 && (
                                <p className="text-sm text-gray-500 mt-1">All brands are already assigned to this OEM</p>
                            )}
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                            {error}
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            onClick={() => {
                                setIsBrandModalOpen(false);
                                setSelectedOemForBrand(null);
                                setNewBrandName('');
                                setSelectedExistingBrandId('');
                                setBrandModalMode('new');
                                setError('');
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveBrand}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
                        >
                            <Plus size={18} />
                            {brandModalMode === 'new' ? 'Create Brand' : 'Assign Brand'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AdminOEMsPage;

